import React, { useState, useEffect, useRef } from 'react';

import {
    FiSearch,
    FiFilter,
    FiEdit2,
    FiSave,
    FiX,
    FiUsers,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiSettings,
    FiMapPin,
    FiCalendar,
    FiUser,
    FiRefreshCw,
    FiDownload,
    FiSlash
} from 'react-icons/fi';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import toast from 'react-hot-toast';

import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { useAuth } from '../../provider/auth.provider';
import { useConfirm } from '../../provider/confirm.provider';
import { SearchableSelect, SelectWithSearch2 } from '../../utils/select-with-search';
import { dateRangeInputBounds, firstDateRangeError, type DateRangeTriple } from '../../utils/date-range.utils';

const SITE_ENGINEER_ASSIGNABLE_ROLE_IDS = [7, 4];

// Types
interface User {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
    employee_code?: string | null;
    role: {
        id: number;
        name: string;
    };
    user_projects?: { project_id: number }[];
}

/**
 * How a user reads in every assignment dropdown on this page.
 *
 * Name alone is not enough to pick the right person: this org has repeated
 * names, so the employee id and the user id are part of the label rather than
 * something you have to open another page to check. They are in the label
 * (not a subtitle) because the select filters on the label text, which makes
 * both ids searchable.
 */
const userOptionLabel = (user: User) =>
    `${user.first_name} ${user.last_name} (${user.role.name}) · Emp: ${user.employee_code || 'N/A'} · ID: ${user.id}`;

interface ProjectSummary {
    id: number;
    project_name: string;
}

type SelectOption = { id: number; label: string; value: number };

interface Site {
    id: number;
    site_id: string;
    project_id: number;
    activity: string;
    reference_number: string;
    client_allocation_date: string | null;
    site_name: string;
    site_address: string;
    opco_name: string;
    opco_id: string;
    state_circle_id: number;
    district_id: number;
    pincode: number;
    site_latitude: string;
    site_longitude: string;
    site_priority: string;
    pr_number: string;
    pr_date: string | null;
    po_number: string;
    po_date: string | null;
    is_po_no_shared_at_allocation: boolean;
    technician_name: string;
    technician_number: string;
    fse_name: string;
    fse_number: string;
    aom_name: string;
    aom_number: string;
    client_project_name: string | null;
    customer_requirement: string;
    site_information1: string | null;
    site_information2: string | null;
    site_information3: string | null;

    // Site Engineer
    site_engineer_assign_date: string | null;
    site_engineer_assign_time: string | null;
    site_engineer_username: string | null;
    site_engineer_userid: number | null;
    site_engineer_work_date: string | null;
    site_unlock_time: string | null;
    site_unlock_latitude: string | null;
    site_unlock_longitude: string | null;
    site_engineer_work_status: string;
    site_engineer_work_remarks: string | null;
    site_engineer_visit_status: string;

    // Technical Associate
    ta_username: string | null;
    ta_userid: number | null;
    ta_work_date: string | null;
    ta_remarks: string | null;
    ta_se_quality_feedback: string | null;

    // MIS
    mis_username: string | null;
    mis_userid: number | null;
    mis_submission_date: string | null;
    mis_remarks: string | null;
    mis_validator_qa_feedback: string | null;

    // Report Designer
    report_designer_username: string | null;
    report_designer_userid: number | null;
    report_designer_work_date: string | null;
    report_designer_remarks: string | null;

    // Validator
    validator_username: string | null;
    validator_userid: number | null;
    validator_work_date: string | null;
    validator_remarks: string | null;
    validator_report_designer_qa_feedback: string | null;

    // Contact Info
    contact_person: string;
    contact_number: string;

    // Status
    status: string;
    survey_workflow_status: string;
    cancel_reason: string | null;
    hold_reason: string | null;
    visit_remarks: string | null;

    // Meta
    created_by: number;
    is_location_verified: boolean;
    location_verification_attempts: number;
    last_location_check: string | null;
    created_at: string;
    updated_at: string;

    // Assigned IDs
    assigned_engineer_id: number | null;
    assigned_ta_id: number | null;
    assigned_report_maker_id: number | null;
    assigned_validator_id: number | null;

    // Nested project
    project: {
        id: number;
        activity_id: number;
        client_id: number;
        state_circle_id: number;
        client_project_id: number | null;
        project_name: string;
        project_short_name: string;
        project_description: string;
        project_manager_id: number;
        team_coordinator_id: number;
        technical_assistant_id: number;
        mis_id: number;
        budget: number | null;
        status: string;
        created_at: string;
        updated_at: string;
        created_by: number;
        suspended_at: string | null;
        suspended_by: string | null;
        client: {
            id: number;
            client_full_name: string;
            client_short_name: string;
            address: string;
            gst_number: string;
            state: string;
            is_active: boolean;
            created_at: string;
            updated_at: string;
            created_by: number;
            suspended_at: string | null;
            suspended_by: string | null;
        };
        activity: {
            id: number;
            activity_name: string;
            activity_description: string;
            status: string;
            created_by: number;
            suspended_at: string | null;
            suspended_by: string | null;
            created_at: string;
            updated_at: string;
        };
    };

    // Nested users
    site_engineer?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };

    ta?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };

    mis?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };

    report_designer?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };

    validator?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };

    team_coordinator?: {
        id: number;
        first_name: string;
        last_name: string;
        username: string;
        email: string;
    };
}

interface Filters {
    // Basic Site Information
    site_id?: string;
    site_name?: string;
    site_address?: string;
    activity_id?: string;
    reference_number?: string;
    project_id?: string;

    // Location and Organization
    state_circle_id?: string;
    district_id?: string;
    pincode?: string;
    opco_name?: string;
    opco_id?: string;

    // Site Priority and Status
    site_priority?: string;
    status?: string;
    survey_workflow_status?: string;

    // Project name search
    project_name?: string;

    // Date Range Filters
    created_from?: string;
    created_to?: string;
    field_work_started_from?: string;
    field_work_started_to?: string;
    field_work_completed_from?: string;
    field_work_completed_to?: string;
    ta_review_from?: string;
    ta_review_to?: string;
    report_completed_from?: string;
    report_completed_to?: string;
    report_finalized_from?: string;
    report_finalized_to?: string;
    sent_to_client_from?: string;
    sent_to_client_to?: string;

    // PO/PR Information
    pr_number?: string;
    po_number?: string;
    is_po_no_shared_at_allocation?: string;

    // Contact Information
    contact_person?: string;
    contact_number?: string;

    // Technician Information
    technician_name?: string;
    technician_number?: string;
    fse_name?: string;
    fse_number?: string;
    aom_name?: string;
    aom_number?: string;

    // Client Information
    client_project_name?: string;
    customer_requirement?: string;

    // Assignment Status Filters
    has_assigned_engineer?: string;
    has_assigned_ta?: string;
    has_assigned_report_maker?: string;
    has_assigned_validator?: string;
    has_assigned_mis?: string;
    blank_filter?: string;
    site_engineer_userid?: string;
    ta_userid?: string;
    mis_userid?: string;
    report_designer_userid?: string;
    validator_userid?: string;

    // Multiple Status Filter
    status_list?: string;
    workflow_status_list?: string;
    priority_list?: string;

    // Stage group filter (UI-only; maps to workflow_status_list on apply)
    stage_group?: string;
    stage_filter?: string;
}

interface SelectWithSearchProps {
    value: SelectOption | null;
    onChange: (option: SelectOption | null) => void;
    options: SelectOption[];
    placeholder: string;
    disabled?: boolean;
    onSearchChange?: (term: string) => void;
    isLoading?: boolean;
    minSearchLength?: number;
}

const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
    value,
    onChange,
    options,
    placeholder,
    disabled = false,
    onSearchChange,
    isLoading = false,
    minSearchLength = 3,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const meetsMinLength = searchTerm.length >= minSearchLength;
    const filteredOptions = meetsMinLength
        ? options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (onSearchChange && term.length >= minSearchLength) {
            onSearchChange(term);
        }
    };

    return (
        <div className="relative">
            <button
                type="button"
                className={`w-full px-3 py-2.5 text-left bg-white border border-gray-300 rounded  shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'
                    }`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
            >
                <span className={value ? 'text-gray-900' : 'text-gray-500'}>
                    {value ? value.label : placeholder}
                </span>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </button>

            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded  shadow-lg max-h-60 overflow-auto">
                    <div className="p-2 border-b">
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded  text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder={`Type at least ${minSearchLength} characters...`}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            autoFocus
                        />
                    </div>
                    <div className="py-1">
                        {!meetsMinLength ? (
                            <p className="px-3 py-2 text-xs text-gray-400 italic">
                                Type at least {minSearchLength} characters to search
                            </p>
                        ) : isLoading ? (
                            <p className="px-3 py-2 text-xs text-gray-400 italic">Searching...</p>
                        ) : (
                            <>
                                <button
                                    className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100"
                                    onClick={() => {
                                        onChange(null);
                                        setIsOpen(false);
                                        setSearchTerm('');
                                    }}
                                >
                                    Clear selection
                                </button>
                                {filteredOptions.length === 0 ? (
                                    <p className="px-3 py-2 text-xs text-gray-400 italic">No users found</p>
                                ) : (
                                    filteredOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-blue-50 hover:text-blue-900"
                                            onClick={() => {
                                                onChange(option);
                                                setIsOpen(false);
                                                setSearchTerm('');
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    ))
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Every from/to pair on the Dates tab, with the heading it is shown under.
const DATE_RANGE_FILTERS: Array<[keyof Filters, keyof Filters, string]> = [
    ['created_from', 'created_to', 'Site Created Date'],
    ['field_work_started_from', 'field_work_started_to', 'Field Work Started Date'],
    ['field_work_completed_from', 'field_work_completed_to', 'Field Work Completed Date'],
    ['ta_review_from', 'ta_review_to', 'TA Review Date'],
    ['report_completed_from', 'report_completed_to', 'Report Completed Date'],
    ['report_finalized_from', 'report_finalized_to', 'Report Finalised Date'],
    ['sent_to_client_from', 'sent_to_client_to', 'Send to Client Date'],
];

// Why these filters cannot be applied (a from/to pair too far apart), or null.
const filtersDateRangeError = (filters: Filters): string | null =>
    firstDateRangeError(
        DATE_RANGE_FILTERS.map(([fromKey, toKey, label]): DateRangeTriple => [filters[fromKey], filters[toKey], label])
    );

// Filter Modal Component
interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    filters: Filters;
    onFiltersChange: (filters: Filters) => void;
    onApply: () => void;
    onReset: () => void;
    projectOptions: SelectOption[];
    activityOptions: SelectOption[];
    siteEngineerOptions: SelectOption[];
    taOptions: SelectOption[];
    misOptions: SelectOption[];
    reportMakerOptions: SelectOption[];
    validatorOptions: SelectOption[];
    stateOptions: SelectOption[];
    districtOptions: SelectOption[];
    onUserSearchChange?: (term: string) => void;
    usersLoading?: boolean;
}

const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    onClose,
    filters,
    onFiltersChange,
    onApply,
    onReset,
    projectOptions,
    activityOptions,
    siteEngineerOptions,
    taOptions,
    misOptions,
    reportMakerOptions,
    validatorOptions,
    stateOptions,
    districtOptions,
    onUserSearchChange,
    usersLoading = false,
}) => {
    const [activeTab, setActiveTab] = useState('basic');

    if (!isOpen) return null;

    const handleInputChange = (field: keyof Filters, value: string) => {
        onFiltersChange({
            ...filters,
            [field]: value || undefined
        });
    };

    const selectedProjectOptions = projectOptions.filter((option) =>
        String(filters.project_id || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
            .includes(String(option.value))
    );

    const handleProjectMultiChange = (options: Array<{ value: string | number; label: string }> = []) => {
        onFiltersChange({
            ...filters,
            project_id: options.length ? options.map((option) => option.value).join(",") : undefined,
        });
    };

    const renderProjectMultiSelect = () => (
        <SearchableSelect
            value={selectedProjectOptions}
            onChange={handleProjectMultiChange}
            options={projectOptions.map((option) => ({ value: option.value, label: option.label }))}
            placeholder="Search projects (min 3 chars)"
            isMulti
            showSelectAllVisible
            minSearchLength={3}
        />
    );

    const STAGE_GROUP_MAP: Record<string, string> = {
        survey_pending: [
            "site_created","engineer_assigned","field_work_in_progress"
        ].join(","),
        field_data_upload_pending: [
            "field_work_in_progress","data_uploaded"
        ].join(","),
        survey_done: [
            "field_work_completed","ta_review_pending","ta_review_completed","ta_rejected",
            "mis_report_assign_pending","report_maker_assigned","report_in_progress",
            "report_completed","report_maker_rejected","validator_assigned",
            "validation_in_progress","validation_completed","validation_rejected",
            "report_finalized","report_sent_to_client"
        ].join(","),
        field_work_approved: [
            "ta_review_completed",
            "mis_report_assign_pending","report_maker_assigned","report_in_progress",
            "report_completed","report_maker_rejected","validator_assigned",
            "validation_in_progress","validation_completed","validation_rejected",
            "report_finalized","report_sent_to_client"
        ].join(","),
        report_completed: [
            "report_completed","report_maker_rejected","validator_assigned",
            "validation_in_progress","validation_completed","validation_rejected",
            "report_finalized","report_sent_to_client"
        ].join(","),
        report_finalized: [
            "report_finalized","report_sent_to_client"
        ].join(","),
        report_preparation_pending: [
            "field_work_completed","ta_review_completed","report_maker_assigned"
        ].join(","),
        validation_pending: [
            "validator_assigned"
        ].join(","),
        report_submission_pending: [
            "field_work_completed","ta_review_completed","report_maker_assigned",
            "report_completed","validator_assigned","report_finalized"
        ].join(","),
    };

    const WORKFLOW_STAGE_FILTER_MAP: Record<string, string> = {
        field_data_upload_pending: "field_work_in_progress,data_uploaded",
        ta_review_pending_filter: "field_work_completed",
        mis_report_assign_pending_filter: "ta_review_completed",
        report_in_progress_filter: "report_maker_assigned",
        validation_pending_filter: "validator_assigned",
    };

    const handleStageGroupChange = (group: string) => {
        onFiltersChange({
            ...filters,
            stage_group: group || undefined,
            stage_filter: undefined,
            survey_workflow_status: undefined,
            workflow_status_list: group ? STAGE_GROUP_MAP[group] : undefined,
        });
    };

    const handleWorkflowStageChange = (value: string) => {
        const mappedStatuses = value ? WORKFLOW_STAGE_FILTER_MAP[value] : undefined;
        onFiltersChange({
            ...filters,
            stage_filter: value || undefined,
            survey_workflow_status: mappedStatuses ? undefined : value || undefined,
            workflow_status_list: mappedStatuses,
            stage_group: undefined,
        });
    };

    const tabs = [
        { id: 'basic', label: 'Site Info', icon: FiSearch },
        { id: 'location', label: 'Location', icon: FiMapPin },
        { id: 'dates', label: 'Dates', icon: FiCalendar },
        { id: 'assignments', label: 'Assignments', icon: FiUser },
        { id: 'status', label: 'Stages', icon: FiSettings }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                            <FiFilter className="w-5 h-5" />
                            Advanced Filters
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 transition-colors"
                        >
                            <FiX className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                                ? 'border-blue-500 text-blue-600 bg-white'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[80vh]">
                    {/* Site Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                                    {renderProjectMultiSelect()}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Site ID</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        placeholder="Enter Site ID"
                                        value={filters.site_id || ''}
                                        onChange={(e) => handleInputChange('site_id', e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        placeholder="Enter Site Name"
                                        value={filters.site_name || ''}
                                        onChange={(e) => handleInputChange('site_name', e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                                    <SelectWithSearch2
                                        value={activityOptions.find((o) => o.id === Number(filters.activity_id)) || null}
                                        onChange={(option) => handleInputChange('activity_id', option ? String(option.id) : '')}
                                        options={activityOptions}
                                        placeholder="Search activity (min 3 chars)"
                                        minSearchLength={3}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        placeholder="Enter Reference Number"
                                        value={filters.reference_number || ''}
                                        onChange={(e) => handleInputChange('reference_number', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* PR/PO Details */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900">PR/PO Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">PR Number</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            placeholder="PR Number"
                                            value={filters.pr_number || ''}
                                            onChange={(e) => handleInputChange('pr_number', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            placeholder="PO Number"
                                            value={filters.po_number || ''}
                                            onChange={(e) => handleInputChange('po_number', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">PO Shared at Allocation</label>
                                        <select
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={filters.is_po_no_shared_at_allocation || ''}
                                            onChange={(e) => handleInputChange('is_po_no_shared_at_allocation', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Location Tab */}
                    {activeTab === 'location' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                                {renderProjectMultiSelect()}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">State Circles</label>
                                <SelectWithSearch
                                    value={stateOptions.find((option) => option.id === Number(filters.state_circle_id)) || null}
                                    onChange={(option) => {
                                        handleInputChange('state_circle_id', option ? String(option.id) : '');
                                        handleInputChange('district_id', '');
                                    }}
                                    options={stateOptions}
                                    placeholder="Select State Circle"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Districts</label>
                                <SelectWithSearch
                                    value={districtOptions.find((option) => option.id === Number(filters.district_id)) || null}
                                    onChange={(option) => handleInputChange('district_id', option ? String(option.id) : '')}
                                    options={districtOptions}
                                    placeholder="Select District"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Pincode"
                                    value={filters.pincode || ''}
                                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">OPCO Name</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="OPCO Name"
                                    value={filters.opco_name || ''}
                                    onChange={(e) => handleInputChange('opco_name', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">OPCO ID</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="OPCO ID"
                                    value={filters.opco_id || ''}
                                    onChange={(e) => handleInputChange('opco_id', e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Dates Tab */}
                    {activeTab === 'dates' && (() => {
                        const renderDateSection = (title: string, fromKey: keyof Filters, toKey: keyof Filters) => {
                            const bounds = dateRangeInputBounds(filters[fromKey], filters[toKey]);
                            return (
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={(filters[fromKey] as string) || ''}
                                            onChange={(e) => handleInputChange(fromKey, e.target.value)}
                                            {...bounds.from}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={(filters[toKey] as string) || ''}
                                            onChange={(e) => handleInputChange(toKey, e.target.value)}
                                            {...bounds.to}
                                        />
                                    </div>
                                </div>
                            </div>
                            );
                        };
                        return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                                    <div className="max-w-sm">
                                        {renderProjectMultiSelect()}
                                    </div>
                                </div>
                                {renderDateSection('Site Created Date', 'created_from', 'created_to')}
                                {renderDateSection('Field Work Started Date', 'field_work_started_from', 'field_work_started_to')}
                                {renderDateSection('Field Work Completed Date', 'field_work_completed_from', 'field_work_completed_to')}
                                {renderDateSection('TA Review Date', 'ta_review_from', 'ta_review_to')}
                                {renderDateSection('Report Completed Date', 'report_completed_from', 'report_completed_to')}
                                {renderDateSection('Report Finalised Date', 'report_finalized_from', 'report_finalized_to')}
                                {renderDateSection('Send to Client Date', 'sent_to_client_from', 'sent_to_client_to')}
                            </div>
                        );
                    })()}

                    {/* Assignments Tab */}
                    {activeTab === 'assignments' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                                {renderProjectMultiSelect()}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned MIS</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.has_assigned_mis || ''}
                                    onChange={(e) => handleInputChange('has_assigned_mis', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Blank Filter</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.blank_filter || ''}
                                    onChange={(e) => handleInputChange('blank_filter', e.target.value)}
                                >
                                    <option value="">All Sites</option>
                                    <option value="unassigned">Fully Unassigned</option>
                                    <option value="any">Any Blank Assignment</option>
                                    <option value="site_engineer">Site Engineer Blank</option>
                                    <option value="ta">TA Blank</option>
                                    <option value="mis">MIS Blank</option>
                                    <option value="report_maker">Report Maker Blank</option>
                                    <option value="validator">Validator Blank</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Engineer</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.has_assigned_engineer || ''}
                                    onChange={(e) => handleInputChange('has_assigned_engineer', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Technical Associate</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.has_assigned_ta || ''}
                                    onChange={(e) => handleInputChange('has_assigned_ta', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Report Maker</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.has_assigned_report_maker || ''}
                                    onChange={(e) => handleInputChange('has_assigned_report_maker', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Has Assigned Validator</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.has_assigned_validator || ''}
                                    onChange={(e) => handleInputChange('has_assigned_validator', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Site Engineer</label>
                                <SelectWithSearch
                                    value={siteEngineerOptions.find((option) => option.id === Number(filters.site_engineer_userid)) || null}
                                    onChange={(option) => handleInputChange('site_engineer_userid', option ? String(option.id) : '')}
                                    options={siteEngineerOptions}
                                    placeholder="Filter by site engineer"
                                    onSearchChange={onUserSearchChange}
                                    isLoading={usersLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Technical Associate</label>
                                <SelectWithSearch
                                    value={taOptions.find((option) => option.id === Number(filters.ta_userid)) || null}
                                    onChange={(option) => handleInputChange('ta_userid', option ? String(option.id) : '')}
                                    options={taOptions}
                                    placeholder="Filter by TA"
                                    onSearchChange={onUserSearchChange}
                                    isLoading={usersLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">MIS</label>
                                <SelectWithSearch
                                    value={misOptions.find((option) => option.id === Number(filters.mis_userid)) || null}
                                    onChange={(option) => handleInputChange('mis_userid', option ? String(option.id) : '')}
                                    options={misOptions}
                                    placeholder="Filter by MIS"
                                    onSearchChange={onUserSearchChange}
                                    isLoading={usersLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Report Maker</label>
                                <SelectWithSearch
                                    value={reportMakerOptions.find((option) => option.id === Number(filters.report_designer_userid)) || null}
                                    onChange={(option) => handleInputChange('report_designer_userid', option ? String(option.id) : '')}
                                    options={reportMakerOptions}
                                    placeholder="Filter by report maker"
                                    onSearchChange={onUserSearchChange}
                                    isLoading={usersLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Validator</label>
                                <SelectWithSearch
                                    value={validatorOptions.find((option) => option.id === Number(filters.validator_userid)) || null}
                                    onChange={(option) => handleInputChange('validator_userid', option ? String(option.id) : '')}
                                    options={validatorOptions}
                                    placeholder="Filter by validator"
                                    onSearchChange={onUserSearchChange}
                                    isLoading={usersLoading}
                                />
                            </div>
                        </div>
                    )}

                    {/* Stages Tab */}
                    {activeTab === 'status' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name (Search)</label>
                                {renderProjectMultiSelect()}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Work Completion Status</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.status || ''}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="hold">On Hold</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Stage Group</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.stage_group || ''}
                                    onChange={(e) => handleStageGroupChange(e.target.value)}
                                >
                                    <option value="">Select Stage Group</option>
                                    <option value="survey_pending">Survey Pending</option>
                                    <option value="field_data_upload_pending">Field Data Upload Pending</option>
                                    <option value="survey_done">Survey Done</option>
                                    <option value="field_work_approved">Field Work Approved</option>
                                    <option value="report_completed">Report Completed</option>
                                    <option value="report_finalized">Report Finalized</option>
                                    <option value="report_preparation_pending">Report Preparation Pending (Field Work Done)</option>
                                    <option value="validation_pending">Validation Pending</option>
                                    <option value="report_submission_pending">Report Submission Pending (Field Work Done)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Site Stage / Workflow Stage</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.stage_filter || filters.survey_workflow_status || ''}
                                    onChange={(e) => handleWorkflowStageChange(e.target.value)}
                                >
                                    <option value="">All Workflow Stages</option>
                                    <option value="site_created">Site Created</option>
                                    <option value="engineer_assigned">Engineer Assigned</option>
                                    <option value="field_work_in_progress">Field Work In Progress</option>
                                    <option value="field_work_completed">Field Work Completed</option>
                                    <option value="field_data_upload_pending">Field Data Upload Pending</option>
                                    <option value="data_uploaded">Site Data WIP</option>
                                    <option value="ta_review_pending_filter">TA Review Pending</option>
                                    <option value="ta_review_completed">TA Review Completed</option>
                                    <option value="mis_report_assign_pending_filter">MIS Report Assign Pending</option>
                                    <option value="report_maker_assigned">Report Maker Assigned</option>
                                    <option value="report_in_progress_filter">Report In Progress</option>
                                    <option value="report_completed">Report Completed</option>
                                    <option value="validator_assigned">Validator Assigned</option>
                                    <option value="validation_pending_filter">Validation Pending</option>
                                    <option value="report_finalized">Report Finalized</option>
                                    <option value="report_sent_to_client">Report Sent To Client</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                                <select
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={filters.site_priority || ''}
                                    onChange={(e) => handleInputChange('site_priority', e.target.value)}
                                >
                                    <option value="">All Priorities</option>
                                    <option value="critical">Critical</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                    <button
                        onClick={onReset}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
                    >
                        <FiRefreshCw className="w-4 h-4" />
                        Reset Filters
                    </button>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                const dateError = filtersDateRangeError(filters);
                                if (dateError) {
                                    toast.error(dateError);
                                    return;
                                }
                                onApply();
                                onClose();
                            }}
                            className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
                        >
                            <FiSearch className="w-4 h-4" />
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SiteAssignment: React.FC = () => {
    const { hasPermission, user } = useAuth();
    const hasTcAssignmentAccess = hasPermission("Site", "assign_tc");
    const hasMisAssignmentAccess = hasPermission("Site", "mis");
    const hasTaAssignmentAccess = hasPermission("Site", "technical_analyst");
    const canManageAssignments = hasTcAssignmentAccess || hasMisAssignmentAccess || hasTaAssignmentAccess;
    // TC: assign all; TA: assign MIS/ReportMaker/Validator; MIS: assign ReportMaker/Validator only
    const canAssignEngineer = hasTcAssignmentAccess;
    const canAssignTA = hasTcAssignmentAccess;
    const canAssignMIS = hasTcAssignmentAccess || hasTaAssignmentAccess;
    const canAssignReportMaker = hasTcAssignmentAccess || hasTaAssignmentAccess || hasMisAssignmentAccess;
    const canAssignValidator = hasTcAssignmentAccess || hasTaAssignmentAccess || hasMisAssignmentAccess;
    // Bulk cancel is the same action as on Project Sites (Site / bulk_cancel):
    // only sites still at Site Created qualify, and the server re-checks both
    // eligibility and scope.
    const hasBulkCancel = hasPermission("Site", "bulk_cancel");
    // The selection column serves every bulk action: assignments and bulk cancel.
    const canBulkSelect = canManageAssignments || hasBulkCancel;
    const { prompt } = useConfirm();
    const [bulkCancelling, setBulkCancelling] = useState(false);
    const [sites, setSites] = useState<Site[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [activities, setActivities] = useState<{ id: number; activity_name: string }[]>([]);
    const [stateCircles, setStateCircles] = useState<{ id: number; state_name: string }[]>([]);
    const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
    const [filters, setFilters] = useState<Filters>({});
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    const [selectedSites, setSelectedSites] = useState<number[]>([]);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [editFormData, setEditFormData] = useState<any>({});
    const [bulkAssignments, setBulkAssignments] = useState({
        site_engineer_userid: undefined,
        ta_userid: undefined,
        mis_userid: undefined,
        report_designer_userid: undefined,
        validator_userid: undefined,
    });
    const [showBulkForm, setShowBulkForm] = useState(false);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportData, setExportData] = useState<any[]>([]);
    const [pagination, setPagination] = useState<PaginationType>({
        offset: 0,
        page: 1,
        limit: 10,
        total_pages: 0,
        total_count: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    });
    const [quickSearch, setQuickSearch] = useState('');

    const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
        field: 'created_at',
        direction: 'DESC'
    });

    const filterAssignmentsByPermission = (assignments: any) => {
        const filteredAssignments: any = {};

        if (canAssignEngineer && assignments.site_engineer_userid !== undefined) {
            filteredAssignments.site_engineer_userid = assignments.site_engineer_userid;
        }
        if (canAssignTA && assignments.ta_userid !== undefined) {
            filteredAssignments.ta_userid = assignments.ta_userid;
        }
        if (canAssignMIS && assignments.mis_userid !== undefined) {
            filteredAssignments.mis_userid = assignments.mis_userid;
        }
        if (canAssignReportMaker && assignments.report_designer_userid !== undefined) {
            filteredAssignments.report_designer_userid = assignments.report_designer_userid;
        }
        if (canAssignValidator && assignments.validator_userid !== undefined) {
            filteredAssignments.validator_userid = assignments.validator_userid;
        }

        return filteredAssignments;
    };

    const selectedWorkflowStatus = selectedSites.length
        ? sites.find(site => site.id === selectedSites[0])?.survey_workflow_status
        : undefined;

    const isBulkCancellable = (site: Site) =>
        site.survey_workflow_status === 'site_created' && site.status !== 'cancelled';

    const canSelectSiteForBulk = (site: Site) => {
        // Bulk cancel is the only bulk action a user without assignment access
        // has, so they may only pick sites it can act on.
        if (!canManageAssignments && !isBulkCancellable(site)) {
            return selectedSites.includes(site.id);
        }
        return !selectedWorkflowStatus || selectedSites.includes(site.id) || site.survey_workflow_status === selectedWorkflowStatus;
    };

    const getPersonName = (person?: { first_name: string; last_name: string } | null) => {
        if (!person) return 'Not assigned';
        return `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Not assigned';
    };

    const isRestrictedSiteExport = user?.role?.id === 9 || user?.role?.id === 10;

    const mapRestrictedSiteForExport = (site: Site) => ({
        site_id: site.site_id || '',
        project_short_name: site.project?.project_short_name || '',
        activity_name: site.project?.activity?.activity_name || (typeof (site as any).activity === 'object' ? (site as any).activity?.activity_name : (site as any).activity) || '',
        site_name: site.site_name || '',
        site_address: site.site_address || '',
        opco_name: site.opco_name || '',
        opco_id: site.opco_id || '',
        state_name: (site as any).state_circle?.state_name || (site.project as any)?.state_circle?.state_name || '',
        pincode: site.pincode || '',
        site_latitude: site.site_latitude || '',
        site_longitude: site.site_longitude || '',
        site_priority: site.site_priority || '',
        client_project_name: site.client_project_name || '',
        customer_requirement: site.customer_requirement || '',
        site_information1: site.site_information1 || '',
        site_information2: site.site_information2 || '',
        site_information3: site.site_information3 || '',
        mis: getPersonName(site.mis),
        report_maker: getPersonName(site.report_designer),
        status: site.status || '',
        survey_workflow_status: site.survey_workflow_status || '',
    });

    const mapSiteForExport = (site: Site) => ({
        "Site ID": site.site_id || '',
        "Project Name": site.project?.project_name || '',
        "Work Completion Status": site.status || '',
        "Site Stage": site.survey_workflow_status?.replace(/_/g, ' ') || '',
        "Site Engineer": getPersonName(site.site_engineer),
        "Technical Associate": getPersonName(site.ta),
        "MIS": getPersonName(site.mis),
        "Report Designer": getPersonName(site.report_designer),
        "Validator": getPersonName(site.validator),
        "TC Name": getPersonName(site.team_coordinator),
        "Field Work Complete Date": (!["site_created", "engineer_assigned", "field_work_in_progress", "data_uploaded"].includes(site.survey_workflow_status) && site.site_engineer_work_date && site.site_engineer_work_date !== site.site_unlock_time) ? (formatDate(site.site_engineer_work_date) || '') : '',
        "TA Review Date": formatDate(site.ta_work_date) || '',
    });

    // API functions
    const fetchSites = async (
        currentFilters: Filters,
        page: number = 1,
        limit?: number,
        sort: { field: string; direction: 'ASC' | 'DESC' } = sortConfig
    ): Promise<boolean> => {
        // `filters` is draft and applied state at once, so every path that
        // refetches (paging, sorting, chips) is checked here.
        const dateError = filtersDateRangeError(currentFilters);
        if (dateError) {
            toast.error(dateError);
            return false;
        }
        setLoading(true);
        try {
            // Build query parameters object
            const queryParams: any = {
                page: page,
                limit: limit || pagination.limit,
                ...currentFilters
            };

            if (sort?.field) {
                queryParams.sort_by = sort.field;
                queryParams.sort_order = sort.direction;
            }

            // Remove undefined/null values and convert to strings
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === undefined || queryParams[key] === null) {
                    delete queryParams[key];
                } else {
                    queryParams[key] = queryParams[key].toString();
                }
            });

            const response = await axiosInstance.get('/survey/sites/assignment', { params: queryParams });

            if (response.data.success) {
                const sitesData = response.data.data.sites || [];
                setSites(sitesData);
                setPagination(response.data.data.pagination);
                return sitesData.length > 0;
            } else {
                console.error('API returned success: false');
                return false;
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async (search: string = '') => {
        setUsersLoading(true);
        try {
            const assignableRoleIds = [...SITE_ENGINEER_ASSIGNABLE_ROLE_IDS, 5, 6, 9, 10];
            const queryParams: any = {
                page: 1,
                limit: 200,
                src: 'all',
                role_id: assignableRoleIds,
            };
            if (search) {
                queryParams.search = search;
            }

            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === undefined || queryParams[key] === null) {
                    delete queryParams[key];
                } else {
                    queryParams[key] = queryParams[key].toString();
                }
            });

            const response = await axiosInstance.get('/users/list', { params: queryParams });

            if (response.data.success) {
                setUsers(response.data.data.users || []);
            } else {
                console.error('API returned success: false');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleUserSearch = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onUserSearchChange = (term: string) => {
        if (handleUserSearch.current) clearTimeout(handleUserSearch.current);
        handleUserSearch.current = setTimeout(() => {
            fetchUsers(term);
        }, 350);
    };

    const fetchProjects = async () => {
        try {
            const params: Record<string, string | number | number[]> = { limit: 500 };
            if (user?.role?.level !== 1 && user?.id) {
                params.assigned_user_ids = [user.id];
            }
            const response = await axiosInstance.get('/projects/list', {
                params,
            });
            if (response.data.success) {
                setProjects(response.data.data.projects || []);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const fetchActivities = async () => {
        try {
            const response = await axiosInstance.get('/activities/list', { params: { limit: 100, status: 'ACTIVE' } });
            if (response.data.success) {
                setActivities(response.data.data.activities || []);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    };

    const fetchStateCircles = async () => {
        try {
            const response = await axiosInstance.get('/state-circle/list', { params: { limit: 500 } });
            if (response.data.success) {
                setStateCircles(response.data.data.states || []);
            }
        } catch (error) {
            console.error('Error fetching state circles:', error);
        }
    };

    const fetchDistricts = async (stateCircleId?: string) => {
        if (!stateCircleId) { setDistricts([]); return; }
        try {
            const response = await axiosInstance.get(`/state-circle/${stateCircleId}/districts`);
            if (response.data.success) {
                setDistricts(response.data.data.districts || response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching districts:', error);
        }
    };

    const updateSiteAssignments = async (siteIds: number[], assignments: any) => {
        try {
            const response = await axiosInstance.put('/survey/sites/assign', {
                site_ids: siteIds,
                ...assignments
            });

            if (response.data.success) {
                toast.success('Assignments updated');
                // Refresh sites data
                fetchSites(filters, pagination.page, undefined, sortConfig);
                return true;
            }
            toast.error(response.data?.message || 'Failed to update assignments');
            return false;
        } catch (error: any) {
            // The server refuses an assignment for reasons the assigner has to
            // read — an engineer whose field work is open past its activity's
            // deadline (409), a site at the wrong workflow stage (400). Failing
            // silently here made those look like nothing happened at all.
            console.error('Error updating assignments:', error);
            toast.error(
                error?.response?.data?.message || 'Failed to update assignments',
            );
            return false;
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchActivities();
        fetchStateCircles();
        fetchSites(filters);
    }, []);

    const quickSearchInitialized = useRef(false);

    const fetchWithAppliedFilters = async (filterObj: Filters) => {
        setFilters(filterObj);
        await fetchSites(filterObj, 1, undefined, sortConfig);
    };

    const runQuickSearch = async (value: string) => {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
            await fetchWithAppliedFilters({});
            return;
        }

        // Multiple space-separated site IDs
        const parts = trimmedValue.split(/\s+/).filter(Boolean);
        if (parts.length > 1) {
            const multiFilters: Filters = { site_id_list: parts.join(',') } as any;
            await fetchWithAppliedFilters(multiFilters);
            return;
        }

        const siteIdFilters: Filters = { site_id: trimmedValue };
        const siteNameFilters: Filters = { site_name: trimmedValue };
        const projectNameFilters: Filters = { project_name: trimmedValue };
        const isLikelySiteId = /^[A-Za-z0-9._-]+$/.test(trimmedValue);

        if (isLikelySiteId) {
            const found = await fetchSites(siteIdFilters, 1, undefined, sortConfig);
            if (found) {
                setFilters(siteIdFilters);
                return;
            }

            const found2 = await fetchSites(siteNameFilters, 1, undefined, sortConfig);
            if (found2) {
                setFilters(siteNameFilters);
                return;
            }

            await fetchWithAppliedFilters(projectNameFilters);
        } else {
            const found = await fetchSites(siteNameFilters, 1, undefined, sortConfig);
            if (found) {
                setFilters(siteNameFilters);
                return;
            }

            const found2 = await fetchSites(projectNameFilters, 1, undefined, sortConfig);
            if (found2) {
                setFilters(projectNameFilters);
                return;
            }

            await fetchWithAppliedFilters(siteIdFilters);
        }
    };

    useEffect(() => {
        if (!quickSearchInitialized.current) {
            quickSearchInitialized.current = true;
            return;
        }

        const handler = setTimeout(() => {
            runQuickSearch(quickSearch);
        }, 400);

        return () => clearTimeout(handler);
    }, [quickSearch]);

    const handleApplyFilters = () => {
        setPagination((prev: any) => ({ ...prev, page: 1 }));
        setIsFilterModalOpen(false);
        fetchSites(filters, 1, undefined, sortConfig);
    };

    const handleResetFilters = () => {
        setFilters({});
        setQuickSearch('');
        setIsFilterModalOpen(false);
        fetchSites({}, 1, undefined, sortConfig);
    };

    // const handleQuickSearch = () => {
    //     const newFilters = quickSearch ? {
    //         site_id: quickSearch,
    //         site_name: quickSearch
    //     } : {};
    //     setFilters(newFilters);
    //     fetchSites(newFilters, 1, undefined, sortConfig);
    // };

    const handleSelectSite = (siteId: number) => {
        const site = sites.find(item => item.id === siteId);
        if (site && !selectedSites.includes(siteId) && !canSelectSiteForBulk(site)) {
            return;
        }

        setSelectedSites((prev: any) =>
            prev.includes(siteId)
                ? prev.filter((id: number) => id !== siteId)
                : [...prev, siteId]
        );
    };

    const handleSelectAll = () => {
        const selectableSites = sites.filter(site =>
            canSelectSiteForBulk(site) &&
            (!selectedWorkflowStatus || site.survey_workflow_status === selectedWorkflowStatus)
        );
        if (selectedSites.length === selectableSites.length) {
            setSelectedSites([]);
        } else {
            setSelectedSites(selectableSites.map(site => site.id));
        }
    };

    const handleEditRow = (site: Site) => {
        setEditingRow(site.id);
        setEditFormData({
            site_engineer_userid: site.site_engineer_userid,
            ta_userid: site.ta_userid,
            mis_userid: site.mis_userid,
            report_designer_userid: site.report_designer_userid,
            validator_userid: site.validator_userid
        });
        // Pre-populate users state with existing assigned users so getUserById works
        const assignedUsers: User[] = [];
        const addAssigned = (u: any, roleId: number, roleName: string) => {
            if (u && !users.find(x => x.id === u.id)) {
                assignedUsers.push({ ...u, role: { id: roleId, name: roleName } });
            }
        };
        addAssigned(site.site_engineer, SITE_ENGINEER_ASSIGNABLE_ROLE_IDS[0], 'Site Engineer');
        addAssigned(site.ta, 5, 'Technical Associate');
        addAssigned(site.mis, 6, 'MIS');
        addAssigned(site.report_designer, 9, 'Report Designer');
        addAssigned(site.validator, 10, 'Validator');
        if (assignedUsers.length > 0) {
            setUsers(prev => {
                const ids = new Set(prev.map(u => u.id));
                return [...prev, ...assignedUsers.filter(u => !ids.has(u.id))];
            });
        }
    };

    const handleSaveRow = async () => {
        if (editingRow) {
            const assignments = filterAssignmentsByPermission(editFormData);
            if (!Object.keys(assignments).length) {
                return;
            }

            const success = await updateSiteAssignments([editingRow], assignments);
            if (success) {
                setEditingRow(null);
                setEditFormData({});
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleBulkUpdate = async () => {
        if (selectedSites.length > 0) {
            const assignments = filterAssignmentsByPermission(bulkAssignments);
            if (!Object.keys(assignments).length) {
                return;
            }

            const success = await updateSiteAssignments(selectedSites, assignments);
            if (success) {
                setSelectedSites([]);
                setShowBulkForm(false);
                setBulkAssignments({
                    site_engineer_userid: undefined,
                    ta_userid: undefined,
                    mis_userid: undefined,
                    report_designer_userid: undefined,
                    validator_userid: undefined,
                });
            }
        }
    };

    /**
     * Bulk cancel - offered only when the whole selection is at Site Created.
     * The server skips (and names back) anything already cancelled or further
     * along, so a stale page can never cancel a site that has moved on.
     */
    const canBulkCancel =
        hasBulkCancel &&
        selectedSites.length > 0 &&
        selectedWorkflowStatus === 'site_created' &&
        selectedSites.every(id => {
            const site = sites.find(item => item.id === id);
            return !site || site.status !== 'cancelled';
        });

    const handleBulkCancel = async () => {
        if (selectedSites.length === 0) return;
        const count = selectedSites.length;
        const reason = await prompt({
            title: `Cancel ${count} site${count === 1 ? '' : 's'}?`,
            message: `${count} site${count === 1 ? '' : 's'} at Site Created will be cancelled. Sites already cancelled or further along are skipped.`,
            label: 'Cancel reason',
            placeholder: 'Why are these sites being cancelled?',
            required: true,
            multiline: true,
            confirmText: 'Cancel sites',
            cancelText: 'Go back',
            variant: 'warning',
        });
        if (reason === null) return;

        setBulkCancelling(true);
        try {
            const response = await axiosInstance.post('/survey/sites/bulk/cancel', {
                site_ids: selectedSites,
                cancel_reason: reason,
            });
            if (response.data.success) {
                const { updated_count = 0, skipped = [] } = response.data.data || {};
                if (updated_count > 0) {
                    toast.success(`${updated_count} site${updated_count === 1 ? '' : 's'} cancelled`);
                }
                if (Array.isArray(skipped) && skipped.length > 0) {
                    toast.error(
                        `${skipped.length} site${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0].reason}`,
                    );
                }
                setSelectedSites([]);
                setShowBulkForm(false);
                await fetchSites(filters, pagination.page, undefined, sortConfig);
            } else {
                toast.error(response.data?.message || 'Failed to cancel sites');
            }
        } catch (error: any) {
            console.error('Error bulk cancelling sites:', error);
            toast.error(error?.response?.data?.message || 'Failed to cancel sites');
        } finally {
            setBulkCancelling(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPagination((prev: any) => ({ ...prev, page: newPage }));
        fetchSites(filters, newPage, undefined, sortConfig);
    };

    const handleLimitChange = (newLimit: number) => {
        setPagination((prev: any) => ({ ...prev, limit: newLimit, page: 1 }));
        fetchSites(filters, 1, newLimit, sortConfig);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const handleSort = (field: string) => {
        const direction: 'ASC' | 'DESC' =
            sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
        const updatedSort: { field: string; direction: 'ASC' | 'DESC' } = { field, direction };
        setSortConfig(updatedSort);
        fetchSites(filters, pagination.page, undefined, updatedSort);
    };

    const fetchExportData = async () => {
        const dateError = filtersDateRangeError(filters);
        if (dateError) {
            toast.error(dateError);
            return;
        }
        try {
            setExporting(true);
            const allSites: Site[] = [];
            let currentPage = 1;
            let totalPages = 1;
            const pageSize = 500;

            do {
                const queryParams: any = {
                    page: currentPage,
                    limit: pageSize,
                    ...filters,
                    sort_by: sortConfig.field,
                    sort_order: sortConfig.direction,
                };

                Object.keys(queryParams).forEach((key) => {
                    if (queryParams[key] === undefined || queryParams[key] === null || queryParams[key] === '') {
                        delete queryParams[key];
                    } else {
                        queryParams[key] = queryParams[key].toString();
                    }
                });

                const response = await axiosInstance.get('/survey/sites/assignment', { params: queryParams });
                if (!response.data.success) {
                    break;
                }

                const currentSites: Site[] = response.data.data.sites || [];
                allSites.push(...currentSites);
                totalPages = response.data.data.pagination?.total_pages || currentPage;
                currentPage += 1;
            } while (currentPage <= totalPages);

            setExportData(isRestrictedSiteExport ? allSites.map(mapRestrictedSiteForExport) : allSites.map(mapSiteForExport));
            setShowExcelModal(true);
        } catch (error) {
            console.error('Error exporting assignment sites:', error);
        } finally {
            setExporting(false);
        }
    };

    const getSortIcon = (field: string) => {
        if (!sortConfig || sortConfig.field !== field) {
            return <FaSort className="w-3 h-3 text-gray-400" />;
        }
        return sortConfig.direction === 'ASC' ? (
            <FaSortUp className="w-3 h-3 text-blue-600" />
        ) : (
            <FaSortDown className="w-3 h-3 text-blue-600" />
        );
    };

    const getUserOptions = (roleIds: number | number[]) => {
        const allowedRoleIds = Array.isArray(roleIds) ? roleIds : [roleIds];

        return users.filter(user => allowedRoleIds.includes(user.role.id))
            .map(user => ({
                id: user.id,
                label: userOptionLabel(user),
                value: user.id
            }));
    };

    const getValidatorOptions = () => {
        const selectedProjectIds = Array.from(new Set(
            selectedSites
                .map(siteId => sites.find(s => s.id === siteId)?.project_id)
                .filter((pid): pid is number => pid != null)
        ));
        const validatorRoleIds = [2, 3, 4, 5, 6, 10];
        return users
            .filter(u => validatorRoleIds.includes(u.role.id))
            .filter(u =>
                selectedProjectIds.length === 0 ||
                u.user_projects?.some(up => selectedProjectIds.includes(up.project_id))
            )
            .map(u => ({
                id: u.id,
                label: userOptionLabel(u),
                value: u.id
            }));
    };

    const getUserById = (id: number | undefined) => {
        if (!id) return null;
        const user = users.find(u => u.id === id);
        if (!user) return null;
        return {
            id: user.id,
            label: userOptionLabel(user),
            value: user.id
        };
    };

    const projectOptions: SelectOption[] = projects.map((project) => ({
        id: project.id,
        label: project.project_name,
        value: project.id,
    }));

    const activityOptions: SelectOption[] = activities.map((a) => ({
        id: a.id,
        label: a.activity_name,
        value: a.id,
    }));

    const formatFilterValue = (key: keyof Filters, value: string) => {
        if (key === 'project_id') {
            const labels = String(value)
                .split(',')
                .map((id) => projectOptions.find((option) => option.id === Number(id.trim()))?.label)
                .filter(Boolean);
            return labels.length ? labels.join(', ') : value;
        }

        if (key === 'activity_id') {
            return activityOptions.find((option) => option.id === Number(value))?.label || value;
        }

        if (key === 'site_engineer_userid' || key === 'ta_userid' || key === 'mis_userid' || key === 'validator_userid') {
            return getUserById(Number(value))?.label || value;
        }

        if (key === 'blank_filter') {
            const labelMap: Record<string, string> = {
                any: 'Any Blank Assignment',
                site_engineer: 'Site Engineer Blank',
                ta: 'TA Blank',
                mis: 'MIS Blank',
                validator: 'Validator Blank',
            };
            return labelMap[value] || value;
        }

        return value;
    };

    const getActiveFiltersCount = () => {
        return Object.keys(filters).filter(key => filters[key as keyof Filters]).length;
    };

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Site Assignment</h1>
                            <p className="text-gray-600 mt-1">Manage and assign sites to team members efficiently</p>
                        </div>
                    </div>

                    {/* Quick Search and Filter Bar */}
                    <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-6">
                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                            <div className="flex-1 max-w-md">
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Quick search by Site ID, Site name or Project name"
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        value={quickSearch}
                                        onChange={(e) => setQuickSearch(e.target.value)}
                                        // onKeyPress={(e) => e.key === 'Enter' && handleQuickSearch()}
                                    />
                                </div>
                            </div>
                            <div className="w-full lg:w-56">
                                <SearchableSelect
                                    value={projectOptions
                                        .filter((project) => String(filters.project_id || "").split(",").includes(String(project.value)))
                                        .map((project) => ({ value: project.value, label: project.label }))}
                                    onChange={(options: Array<{ value: string | number; label: string }>) => {
                                        const newFilters = {
                                            ...filters,
                                            project_id: options.length ? options.map((option) => option.value).join(",") : undefined,
                                        }
                                        setFilters(newFilters)
                                        fetchSites(newFilters, 1, undefined, sortConfig)
                                    }}
                                    options={projectOptions.map((project) => ({ value: project.value, label: project.label }))}
                                    placeholder="All Projects (min 3 chars)"
                                    isMulti
                                    showSelectAllVisible
                                    minSearchLength={3}
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={fetchExportData}
                                    disabled={exporting}
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center gap-2 transition-colors disabled:cursor-not-allowed disabled:bg-green-400"
                                >
                                    <FiDownload className="w-4 h-4" />
                                    {exporting ? 'Preparing...' : 'Export'}
                                </button>
                                <button
                                    onClick={() => setIsFilterModalOpen(true)}
                                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center gap-2 transition-colors relative"
                                >
                                    <FiFilter className="w-4 h-4" />
                                    Advanced Filters
                                    {getActiveFiltersCount() > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            {getActiveFiltersCount()}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Active Filters Display */}
                        {getActiveFiltersCount() > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                                    {Object.entries(filters).map(([key, value]) =>
                                        value && (
                                            <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {key.replace(/_/g, ' ')}: {formatFilterValue(key as keyof Filters, value)}
                                                <button
                                                    onClick={() => {
                                                        const newFilters = { ...filters };
                                                        delete newFilters[key as keyof Filters];
                                                        setFilters(newFilters);
                                                        fetchSites(newFilters, 1, undefined, sortConfig);
                                                    }}
                                                    className="ml-1 text-blue-600 hover:text-blue-800"
                                                >
                                                    <FiX className="w-3 h-3" />
                                                </button>
                                            </span>
                                        )
                                    )}
                                    <button
                                        onClick={handleResetFilters}
                                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                                    >
                                        Clear all
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    filters={filters}
                    onFiltersChange={(f) => {
                        setFilters(f);
                        // Reload districts when state changes
                        if (f.state_circle_id !== filters.state_circle_id) {
                            fetchDistricts(f.state_circle_id);
                        }
                    }}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                    projectOptions={projectOptions}
                    activityOptions={activityOptions}
                    siteEngineerOptions={getUserOptions(SITE_ENGINEER_ASSIGNABLE_ROLE_IDS)}
                    taOptions={getUserOptions(5)}
                    misOptions={getUserOptions(6)}
                    reportMakerOptions={getUserOptions(9)}
                    validatorOptions={getUserOptions(10)}
                    stateOptions={stateCircles.map(s => ({ id: s.id, label: s.state_name, value: s.id }))}
                    districtOptions={districts.map(d => ({ id: d.id, label: d.name, value: d.id }))}
                    onUserSearchChange={onUserSearchChange}
                    usersLoading={usersLoading}
                />

                {/* Bulk Actions */}
                {canBulkSelect && selectedSites.length > 0 && (
                    <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <FiUsers className="w-5 h-5 text-blue-800" />
                                        <span className="text-sm font-semibold text-blue-900">
                                            {selectedSites.length} site{selectedSites.length !== 1 ? 's' : ''} selected
                                            {selectedWorkflowStatus ? ` (${selectedWorkflowStatus.replace(/_/g, ' ')})` : ''}
                                        </span>
                                    </div>
                                    <div className="h-4 w-px bg-gray-300"></div>
                                    <button
                                        onClick={() => setSelectedSites([])}
                                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                                    >
                                        Clear selection
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {canManageAssignments && (
                                        <button
                                            onClick={() => setShowBulkForm(!showBulkForm)}
                                            className="bg-blue-800 text-white px-6 py-2 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium transition-colors flex items-center gap-2"
                                        >
                                            <FiUsers className="w-4 h-4" />
                                            Bulk Assign
                                        </button>
                                    )}
                                    {canBulkCancel && (
                                        <button
                                            onClick={handleBulkCancel}
                                            disabled={bulkCancelling}
                                            className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <FiSlash className="w-4 h-4" />
                                            {bulkCancelling ? 'Cancelling…' : 'Bulk Cancel'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {canManageAssignments && showBulkForm && (
                                <div className="mt-6 p-6 bg-white rounded border border-gray-200 shadow-sm">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <FiSettings className="w-5 h-5" />
                                        Bulk Assignment Configuration
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {canAssignEngineer && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Site Engineer</label>
                                                <SelectWithSearch
                                                    value={getUserById(bulkAssignments.site_engineer_userid)}
                                                    onChange={(option) => setBulkAssignments((prev: any) => ({ ...prev, site_engineer_userid: option?.id }))}
                                                    options={getUserOptions(SITE_ENGINEER_ASSIGNABLE_ROLE_IDS)}
                                                    placeholder="Select site engineer"
                                                    disabled={false}
                                                    onSearchChange={onUserSearchChange}
                                                    isLoading={usersLoading}
                                                />
                                            </div>
                                        )}

                                        {canAssignTA && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Technical Associate</label>
                                                <SelectWithSearch
                                                    value={getUserById(bulkAssignments.ta_userid)}
                                                    onChange={(option) => setBulkAssignments((prev: any) => ({ ...prev, ta_userid: option?.id }))}
                                                    options={getUserOptions(5)}
                                                    placeholder="Select Technical Associate"
                                                    disabled={false}
                                                    onSearchChange={onUserSearchChange}
                                                    isLoading={usersLoading}
                                                />
                                            </div>
                                        )}

                                        {canAssignMIS && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">MIS</label>
                                                <SelectWithSearch
                                                    value={getUserById(bulkAssignments.mis_userid)}
                                                    onChange={(option) => setBulkAssignments((prev: any) => ({ ...prev, mis_userid: option?.id }))}
                                                    options={getUserOptions(6)}
                                                    placeholder="Select MIS"
                                                    disabled={false}
                                                    onSearchChange={onUserSearchChange}
                                                    isLoading={usersLoading}
                                                />
                                            </div>
                                        )}

                                        {canAssignReportMaker && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Report Designer</label>
                                                <SelectWithSearch
                                                    value={getUserById(bulkAssignments.report_designer_userid)}
                                                    onChange={(option) => setBulkAssignments((prev: any) => ({ ...prev, report_designer_userid: option?.id }))}
                                                    options={getUserOptions(9)}
                                                    placeholder="Select report designer"
                                                    disabled={false}
                                                    onSearchChange={onUserSearchChange}
                                                    isLoading={usersLoading}
                                                />
                                            </div>
                                        )}

                                        {canAssignValidator && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Validator</label>
                                                <SelectWithSearch
                                                    value={getUserById(bulkAssignments.validator_userid)}
                                                    onChange={(option) => setBulkAssignments((prev: any) => ({ ...prev, validator_userid: option?.id }))}
                                                    options={getValidatorOptions()}
                                                    placeholder="Select validator"
                                                    disabled={false}
                                                    onSearchChange={onUserSearchChange}
                                                    isLoading={usersLoading}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={handleBulkUpdate}
                                            className="bg-blue-800 text-white px-6 py-2.5 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium transition-colors flex items-center gap-2"
                                        >
                                            <FiSave className="w-4 h-4" />
                                            Update Selected Sites
                                        </button>
                                        <button
                                            onClick={() => setShowBulkForm(false)}
                                            className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Sites Table */}
                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Sites Overview</h2>
                            <div className="text-sm text-gray-500">
                                {loading ? 'Loading...' : `${sites.length} sites displayed`}
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    {canBulkSelect && (
                                        <th className="px-6 py-4 text-left">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                checked={selectedSites.length > 0 && sites.length > 0 && sites.every(site => !canSelectSiteForBulk(site) || selectedSites.includes(site.id))}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                    )}
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('site_id')}
                                        >
                                            <span>Site ID</span>
                                            {getSortIcon('site_id')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('project_id')}
                                        >
                                            <span>Project Name</span>
                                            {getSortIcon('project_id')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">
                                        Activity
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('status')}
                                        >
                                            <span>Work Completion Status</span>
                                            {getSortIcon('status')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('survey_workflow_status')}
                                        >
                                            <span>Site Stage</span>
                                            {getSortIcon('survey_workflow_status')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('site_engineer_userid')}
                                        >
                                            <span>Site Engineer</span>
                                            {getSortIcon('site_engineer_userid')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('ta_userid')}
                                        >
                                            <span>Technical Associate</span>
                                            {getSortIcon('ta_userid')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('mis_userid')}
                                        >
                                            <span>MIS</span>
                                            {getSortIcon('mis_userid')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('report_designer_userid')}
                                        >
                                            <span>Report Designer</span>
                                            {getSortIcon('report_designer_userid')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('validator_userid')}
                                        >
                                            <span>Validator</span>
                                            {getSortIcon('validator_userid')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">
                                        TC Name
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[170px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('site_engineer_work_date')}
                                        >
                                            <span>Field Work Complete Date</span>
                                            {getSortIcon('site_engineer_work_date')}
                                        </button>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-gray-700"
                                            onClick={() => handleSort('ta_work_date')}
                                        >
                                            <span>TA Review Date</span>
                                            {getSortIcon('ta_work_date')}
                                        </button>
                                    </th>
                                    {canManageAssignments && (
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    )}
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={13 + (canBulkSelect ? 1 : 0) + (canManageAssignments ? 1 : 0)} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
                                                <span className="text-gray-500">Loading sites...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : sites.length === 0 ? (
                                    <tr>
                                        <td colSpan={13 + (canBulkSelect ? 1 : 0) + (canManageAssignments ? 1 : 0)} className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="text-gray-400">
                                                    <FiSearch className="w-12 h-12 mx-auto mb-2" />
                                                </div>
                                                <span className="text-gray-500">
                                                    No sites found matching your criteria
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        setFilters({});
                                                        setQuickSearch('');
                                                        fetchSites({}, 1);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                                                >
                                                    Clear filters to see all sites
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    sites.map((site) => (
                                        <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                                            {/* Select Checkbox */}
                                            {canBulkSelect && (
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="checkbox"
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedSites.includes(site.id)}
                                                        disabled={!canSelectSiteForBulk(site)}
                                                        onChange={() => handleSelectSite(site.id)}
                                                        title={!canSelectSiteForBulk(site)
                                                            ? (canManageAssignments ? 'Bulk actions require the same workflow stage' : 'Only sites at Site Created can be bulk cancelled')
                                                            : 'Select site'}
                                                    />
                                                </td>
                                            )}

                                            {/* Site Info */}
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="text-sm font-semibold text-gray-900">
                                                        {site.site_id}
                                                    </div>
                                                    <div className="text-sm text-gray-600">{site.site_name}</div>
                                                </div>
                                            </td>

                                            {/* Project Name */}
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">{site.project?.project_name || '—'}</div>
                                                <div className="text-xs text-gray-500">{site.project?.project_short_name || ''}</div>
                                            </td>

                                            {/* Activity */}
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {site.project?.activity?.activity_name || (typeof (site as any).activity === 'object' ? (site as any).activity?.activity_name : (site as any).activity) || '—'}
                                                </div>
                                            </td>

                                            {/* Work Completion Status */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                                                    site.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    site.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                                    site.status === 'hold' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-blue-100 text-blue-800'
                                                }`}>
                                                    {site.status || '—'}
                                                </span>
                                            </td>

                                            {/* Site Stage */}
                                            <td className="px-6 py-4">
                                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                                                    {site.survey_workflow_status?.replace(/_/g, ' ') || '—'}
                                                </span>
                                            </td>

                                            {editingRow === site.id ? (
                                                <>
                                                    {/* Editable Mode */}
                                                    <td className="px-6 py-4 min-w-[200px]">
                                                        <SelectWithSearch
                                                            value={getUserById(editFormData.site_engineer_userid)}
                                                            onChange={(option) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    site_engineer_userid: option?.id,
                                                                }))
                                                            }
                                                            options={getUserOptions(SITE_ENGINEER_ASSIGNABLE_ROLE_IDS)}
                                                            placeholder="Select engineer"
                                                            disabled={false}
                                                            onSearchChange={onUserSearchChange}
                                                            isLoading={usersLoading}
                                                        />
                                                    </td>

                                                    <td className="px-6 py-4 min-w-[200px]">
                                                        <SelectWithSearch
                                                            value={getUserById(editFormData.ta_userid)}
                                                            onChange={(option) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    ta_userid: option?.id,
                                                                }))
                                                            }
                                                            options={getUserOptions(5)}
                                                            placeholder="Select Technical Associate"
                                                            disabled={false}
                                                            onSearchChange={onUserSearchChange}
                                                            isLoading={usersLoading}
                                                        />
                                                    </td>

                                                    <td className="px-6 py-4 min-w-[200px]">
                                                        <SelectWithSearch
                                                            value={getUserById(editFormData.mis_userid)}
                                                            onChange={(option) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    mis_userid: option?.id,
                                                                }))
                                                            }
                                                            options={getUserOptions(6)}
                                                            placeholder="Select MIS"
                                                            disabled={false}
                                                            onSearchChange={onUserSearchChange}
                                                            isLoading={usersLoading}
                                                        />
                                                    </td>

                                                    <td className="px-6 py-4 min-w-[200px]">
                                                        <SelectWithSearch
                                                            value={getUserById(editFormData.report_designer_userid)}
                                                            onChange={(option) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    report_designer_userid: option?.id,
                                                                }))
                                                            }
                                                            options={getUserOptions(9)}
                                                            placeholder="Select designer"
                                                            disabled={false}
                                                            onSearchChange={onUserSearchChange}
                                                            isLoading={usersLoading}
                                                        />
                                                    </td>

                                                    <td className="px-6 py-4 min-w-[200px]">
                                                        <SelectWithSearch
                                                            value={getUserById(editFormData.validator_userid)}
                                                            onChange={(option) =>
                                                                setEditFormData((prev: any) => ({
                                                                    ...prev,
                                                                    validator_userid: option?.id,
                                                                }))
                                                            }
                                                            options={getUserOptions(10)}
                                                            placeholder="Select validator"
                                                            disabled={false}
                                                            onSearchChange={onUserSearchChange}
                                                            isLoading={usersLoading}
                                                        />
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Read-Only Mode */}
                                                    <td className="px-6 py-4">
                                                        {site.site_engineer ? (
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-blue-800">
                                                                        {site.site_engineer.first_name.charAt(0)}
                                                                        {site.site_engineer.last_name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {site.site_engineer.first_name}{" "}
                                                                        {site.site_engineer.last_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                Not assigned
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        {site.ta ? (
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-green-800">
                                                                        {site.ta.first_name.charAt(0)}
                                                                        {site.ta.last_name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {site.ta.first_name} {site.ta.last_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                Not assigned
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        {site.mis ? (
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-yellow-800">
                                                                        {site.mis.first_name.charAt(0)}
                                                                        {site.mis.last_name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {site.mis.first_name} {site.mis.last_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                Not assigned
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        {site.report_designer ? (
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-purple-800">
                                                                        {site.report_designer.first_name.charAt(0)}
                                                                        {site.report_designer.last_name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {site.report_designer.first_name}{" "}
                                                                        {site.report_designer.last_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                Not assigned
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        {site.validator ? (
                                                            <div className="flex items-center">
                                                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                                                                    <span className="text-xs font-medium text-orange-800">
                                                                        {site.validator.first_name.charAt(0)}
                                                                        {site.validator.last_name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {site.validator.first_name} {site.validator.last_name}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-gray-400 italic">
                                                                Not assigned
                                                            </span>
                                                        )}
                                                    </td>
                                                </>
                                            )}

                                            {/* TC Name */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {site.team_coordinator ? (
                                                    <span className="text-sm text-gray-900">
                                                        {site.team_coordinator.first_name} {site.team_coordinator.last_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-gray-400 italic">Not assigned</span>
                                                )}
                                            </td>

                                            {/* Field Work Complete Date */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-600">
                                                    {!["site_created", "engineer_assigned", "field_work_in_progress", "data_uploaded"].includes(site.survey_workflow_status) && site.site_engineer_work_date && site.site_engineer_work_date !== site.site_unlock_time ? (formatDate(site.site_engineer_work_date) || <span className="text-gray-400 italic">N/A</span>) : <span className="text-gray-400 italic">N/A</span>}
                                                </span>
                                            </td>

                                            {/* TA Review Date */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-600">
                                                    {formatDate(site.ta_work_date) || <span className="text-gray-400 italic">N/A</span>}
                                                </span>
                                            </td>

                                            {canManageAssignments && (
                                                <td className="px-6 py-4">
                                                    {editingRow === site.id ? (
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={handleSaveRow}
                                                                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                                                title="Save changes"
                                                            >
                                                                <FiSave className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                                                                title="Cancel"
                                                            >
                                                                <FiX className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleEditRow(site)}
                                                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                            title="Assign or reassign users"
                                                        >
                                                            <FiEdit2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                    </div>

                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />

                    <ExcelDownload
                        isOpen={showExcelModal}
                        onClose={() => setShowExcelModal(false)}
                        data={exportData}
                        filename="site_assignment"
                        title="Export Site Assignment Data"
                        description="Download the currently filtered site assignment table."
                    />
                </div>
            </div>
        </div>
    );
};

export default SiteAssignment;
